import { memo, Suspense, lazy } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import SuspenseLoader from '@/components/common/SuspenseLoader';
import { useUserRoles } from '@/hooks/useUserRoles';

// Lazy load admin components
const UserManagementPage = lazy(() => import('@/pages/admin/users/UserManagementPage'));
const CompanyBrandingPage = lazy(() => import('@/pages/admin/CompanyBrandingPage'));

// KB backend admin components
const KBUploadPage = lazy(() => import('@/pages/admin/KBUploadPage'));
const KBCloudImportPage = lazy(() => import('@/pages/admin/KBCloudImportPage'));
const KBReviewQueuePage = lazy(() => import('@/pages/admin/KBReviewQueuePage'));
const KBDocumentsPage = lazy(() => import('@/pages/admin/KBDocumentsPage'));
const KBEntityTypesPage = lazy(() => import('@/pages/admin/KBEntityTypesPage'));
const KBEntityQAPage = lazy(() => import('@/pages/admin/KBEntityQAPage'));

const AdminLoadingSpinner = memo(() => (
  <SuspenseLoader type="spinner" height="min-h-[400px]" message="Loading admin page..." />
));

AdminLoadingSpinner.displayName = 'AdminLoadingSpinner';

// Route configuration for better maintainability
const adminRoutes = {
  '/dashboard/admin/users': UserManagementPage,
  '/dashboard/admin/company': CompanyBrandingPage,
  // KB backend admin routes
  '/dashboard/admin/upload': KBUploadPage,
  '/dashboard/admin/cloud-import': KBCloudImportPage,
  '/dashboard/admin/review-queue': KBReviewQueuePage,
  '/dashboard/admin/knowledge-base': KBDocumentsPage,
  '/dashboard/admin/entity-types': KBEntityTypesPage,
  '/dashboard/admin/entity-qa': KBEntityQAPage,
} as const;

const ROUTE_ROLES: Record<string, readonly string[]> = {
  '/dashboard/admin/users':          ['Admin', 'System Manager'],
  '/dashboard/admin/company':        ['Admin', 'System Manager'],
  '/dashboard/admin/upload':         ['Admin', 'System Manager'],
  '/dashboard/admin/cloud-import':   ['Admin', 'System Manager'],
  '/dashboard/admin/knowledge-base': ['Admin', 'System Manager'],
  '/dashboard/admin/review-queue':   ['System Manager'],
  '/dashboard/admin/entity-types':   ['System Manager'],
  '/dashboard/admin/entity-qa':      ['System Manager'],
};

export const AdminRouter = memo(() => {
  const location = useLocation();
  const pathname = location.pathname as keyof typeof adminRoutes;
  const { roles, isSuperAdmin, loading: rolesLoading } = useUserRoles();

  // Folder Manager moved into the upload page - keep old links working.
  if (location.pathname === '/dashboard/admin/knowledge') {
    return <Navigate to="/dashboard/admin/upload" replace />;
  }

  const Component = adminRoutes[pathname];

  if (!Component) {
    return (
      <div className="text-center text-muted-foreground py-8">
        Admin page not found: {pathname}
      </div>
    );
  }

  const required = ROUTE_ROLES[pathname];
  if (required) {
    const hasAccess = isSuperAdmin || roles?.some(r => required.includes(r.name));
    if (!hasAccess) {
      if (rolesLoading) {
        return <AdminLoadingSpinner />;
      }
      return <Navigate to="/error/403" replace />;
    }
  }

  return (
    <Suspense fallback={<AdminLoadingSpinner />}>
      <Component />
    </Suspense>
  );
});

AdminRouter.displayName = 'AdminRouter';
